// ─────────────────────────────────────────────
//  Firebase Configuration — Anesthesia Team Control
//  Project: anesthesia-team-control
// ─────────────────────────────────────────────
import { initializeApp } from "firebase/app";
import {
  initializeFirestore,
  getFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
} from "firebase/firestore";
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import { isSandboxBoot } from "../sandbox/mode";
import { isTestModeBoot, TEST_MODE_PERSIST_KEY } from "../domain/testMode";
import { createLocalDb } from "../sandbox/localDb";

export const firebaseConfig = {
  apiKey: "AIzaSyANjAyt9jwpanAibw7eAqoSohKjz7RX4zw",
  authDomain: "anesthesia-team-control.firebaseapp.com",
  projectId: "anesthesia-team-control",
  storageBucket: "anesthesia-team-control.firebasestorage.app",
  messagingSenderId: "935701008894",
  appId: "1:935701008894:web:8ca36b11165d2dbad0b0af",
  measurementId: "G-LNX6J2PLP2"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Services
//
// Firestore offline persistence (D-10): initialize with a persistent (IndexedDB)
// local cache so the board keeps working when OR WiFi drops. Single-tab manager:
// the multi-tab manager triggers a watch-stream crash loop in the SDK
// ("INTERNAL ASSERTION FAILED: Unexpected state", IDs ca9/b815 — reproduced on
// 10.14.1 AND 11.10.0, firebase-js-sdk #8474), which kills live listeners
// (surgeons list, rooms). With single-tab, extra tabs hit "failed-precondition"
// and fall back to the in-memory store below — degraded but stable.
//
// Firestore — or, in sandbox mode, a local store wearing the same face.
//
// The choice is made HERE, at boot, and never afterwards: `db` is referenced 90
// times inside firebase/firestore.js, and rebinding it once listeners have
// attached leaves stale onSnapshot subscriptions that fail silently. Entering
// or leaving sandbox sets the flag and reloads.
//
// createLocalDb() is total — it degrades to memory rather than throwing when
// IndexedDB is unavailable — because a throw at module scope in this file kills
// the module graph before React mounts. That is exactly how the first TestFlight
// build rendered a blank white screen.
let db;
if (isSandboxBoot()) {
  db = createLocalDb({ persistKey: "atc-sandbox-v1" });
} else if (isTestModeBoot()) {
  // ── TESTING MODE ──────────────────────────────────────────────────────
  // A snapshot of a REAL facility on a REAL date, captured before entry by
  // features/testing/enterTestMode. Same mechanism as the sandbox, different
  // store and different meaning: the sandbox is a synthetic demo facility,
  // this is somebody's actual Tuesday.
  //
  // ⚠️ SEPARATE PERSIST KEY. Sharing the sandbox's would let a demo boot be
  // served a real hospital's snapshot, or the reverse.
  //
  // ⚠️ THE SANDBOX WINS THE TIE ABOVE. Both flags set is an unreachable state
  // that would mean somebody's data got mixed; resolving it toward the
  // synthetic facility is the harmless direction.
  db = createLocalDb({ persistKey: TEST_MODE_PERSIST_KEY });
} else {
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentSingleTabManager({ forceOwnership: false }),
      }),
    });
  } catch (err) {
    const code = err && err.code;
    if (code === "failed-precondition") {
      console.warn(
        "[firestore] Offline persistence unavailable: multiple tabs open without multi-tab support. Continuing without persistence."
      );
    } else if (code === "unimplemented") {
      console.warn(
        "[firestore] Offline persistence unsupported in this browser. Continuing without persistence."
      );
    } else {
      console.warn(
        "[firestore] Could not enable offline persistence; continuing without it.",
        err
      );
    }
    // Fall back to the default Firestore instance so the app still works.
    // getFirestore returns the already-started instance if initializeFirestore
    // partially ran, avoiding a "Firestore has already been started" throw.
    db = getFirestore(app);
  }
}
export { db };

// Auth — persistence must be declared as a FALLBACK CHAIN, not left to default.
//
// getAuth() resolves to IndexedDB persistence, and IndexedDB is unreliable
// inside the iOS WKWebView when the app is served from a capacitor:// origin.
// When it stalls, Firebase never delivers the first onAuthStateChanged event —
// so the app has no idea whether anyone is signed in and sits on the boot
// screen forever. That is precisely how build 8 hung on iOS.
//
// initializeAuth() takes an ordered list and uses the first that works:
// IndexedDB where it is healthy (normal browsers), localStorage in the
// WebView, and finally memory so a session at least survives the current
// launch. Falling back to memory costs a re-login after force-quit; hanging
// costs the whole app.
let auth;
try {
  auth = initializeAuth(app, {
    persistence: [
      indexedDBLocalPersistence,
      browserLocalPersistence,
      inMemoryPersistence,
    ],
  });
} catch (e) {
  // Already initialized (hot reload, duplicate import) — reuse it.
  auth = getAuth(app);
}
export { auth };
export const storage   = getStorage(app);

// Analytics — MUST be guarded, exactly like messaging below.
//
// getAnalytics() THROWS in environments it does not support, and a Capacitor
// iOS WebView is one of them: the app is served from a capacitor:// origin,
// which has no valid web origin or cookie jar for gtag. An unguarded throw at
// MODULE SCOPE takes the whole module graph down with it — React never mounts
// and the app renders a blank white screen with nothing in the UI to explain
// it. That is exactly how the first TestFlight build failed.
//
// Nothing in the app actually reads this export, so a null is harmless.
// ⚠️ AND THE try/catch ALONE IS NOT ENOUGH, BECAUSE THE FAILURE IS ASYNC.
// getAnalytics() returns synchronously but starts an internal async
// initialisation (`_initializeAnalytics` → `findGtagScriptOnPage`), and a
// rejection from THAT never passes through this catch — a try/catch cannot
// catch a rejection. Under the full test suite the init landed after jsdom had
// been torn down and threw `ReferenceError: window is not defined` as an
// UNHANDLED REJECTION: every one of 5088 tests passed and the run still exited
// 1, which is a red CI build with a green report.
//
// Analytics has no business running in a test environment at all — nothing in
// the app reads this export — so the honest fix is not to start it there.
let analytics = null;
const analyticsWanted =
  typeof window !== "undefined" && process.env.NODE_ENV !== "test";
if (analyticsWanted) {
  try {
    analytics = getAnalytics(app);
  } catch (e) {
    console.warn("[analytics] unavailable in this environment; continuing without it.", e);
  }
}
export { analytics };

// Web push (firebase/messaging) is DELIBERATELY NOT INITIALISED HERE.
//
// It never worked: there is no `firebase-messaging-sw.js` in public/ and never
// was, so `getMessaging(app)` could not have delivered a notification. Push on
// a real device goes through Capacitor's PushNotifications plugin instead
// (src/native/), which is a different transport entirely. Nothing in the app
// imported the export.
//
// What it DID do was reject. `getMessaging()` fails asynchronously on engines
// without the required APIs, so the `try/catch` that used to wrap it caught
// nothing — a synchronous guard cannot catch an async rejection. Measured in
// WebKit (the Safari/iOS engine) against the deployed app: two "Unhandled
// Promise Rejection: FirebaseError: Messaging: This browser doesn't support
// the API's required to use the Firebase SDK" per page load, at module scope
// on the boot path. Chromium and Firefox were clean; only WebKit reported it.
//
// The same rejection had already been papered over in the unit suite with a
// `vi.mock("firebase/messaging")` in src/test/setup.js, which is the tell: it
// was silenced where it was noticed and left live where it was not.
//
// If web push is ever wanted, add the service worker first, then gate the call
// on the ASYNC `isSupported()` from firebase/messaging — never a bare
// getMessaging() at module scope. See the analytics note above: this boot path
// must not throw or reject.

export default app;
